import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AdminAuthProvider } from './context/AdminAuthContext';
import AdminRoute from './components/common/AdminRoute';
import { ToastProvider } from './components/common/Toast';
import LoginPage from './pages/LoginPage';

// Lazy-load protected pages
const DashboardPage          = lazy(() => import('./pages/DashboardPage'));
const AdminUsersPage         = lazy(() => import('./pages/AdminUsersPage'));
const AdminOrgsPage          = lazy(() => import('./pages/AdminOrgsPage'));
const AdminOrgDetailPage     = lazy(() => import('./pages/AdminOrgDetailPage'));
const AdminProjectsPage      = lazy(() => import('./pages/AdminProjectsPage'));
const AdminProjectDetailPage = lazy(() => import('./pages/AdminProjectDetailPage'));
const AdminEnrollmentsPage   = lazy(() => import('./pages/AdminEnrollmentsPage'));
const AdminStageReviewsPage  = lazy(() => import('./pages/AdminStageReviewsPage'));
const AdminStatsPage         = lazy(() => import('./pages/AdminStatsPage'));
const AdminPricingPage       = lazy(() => import('./pages/AdminPricingPage'));
const AdminTeamPage          = lazy(() => import('./pages/AdminTeamPage'));

const PageLoader = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', background: '#f5f7fa', color: '#64748b',
    fontSize: '15px', fontFamily: 'Inter, sans-serif',
  }}>
    Loading…
  </div>
);

function App() {
  return (
    <Router>
      <AdminAuthProvider>
        <ToastProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Public ─────────────────────────────────────── */}
            <Route path="/login" element={<LoginPage />} />

            {/* ── Protected admin routes with role guards ─────── */}
            <Route path="/" element={
              <AdminRoute roles={['super_admin', 'marketing']}><DashboardPage /></AdminRoute>
            } />
            <Route path="/users" element={
              <AdminRoute roles={['super_admin', 'project_reviewer', 'support_agent', 'marketing']}><AdminUsersPage /></AdminRoute>
            } />
            <Route path="/orgs" element={
              <AdminRoute roles={['super_admin']}><AdminOrgsPage /></AdminRoute>
            } />
            <Route path="/orgs/:id" element={
              <AdminRoute roles={['super_admin']}><AdminOrgDetailPage /></AdminRoute>
            } />
            <Route path="/projects" element={
              <AdminRoute roles={['super_admin', 'project_reviewer', 'marketing']}><AdminProjectsPage /></AdminRoute>
            } />
            <Route path="/projects/:id" element={
              <AdminRoute roles={['super_admin', 'project_reviewer', 'marketing']}><AdminProjectDetailPage /></AdminRoute>
            } />
            <Route path="/reviews" element={
              <AdminRoute roles={['super_admin', 'project_reviewer']}><AdminStageReviewsPage /></AdminRoute>
            } />
            <Route path="/enrollments" element={
              <AdminRoute roles={['super_admin', 'project_reviewer', 'marketing']}><AdminEnrollmentsPage /></AdminRoute>
            } />
            <Route path="/stats" element={
              <AdminRoute roles={['super_admin']}><AdminStatsPage /></AdminRoute>
            } />
            <Route path="/pricing" element={
              <AdminRoute roles={['super_admin']}><AdminPricingPage /></AdminRoute>
            } />
            <Route path="/team" element={
              <AdminRoute roles={['super_admin']}><AdminTeamPage /></AdminRoute>
            } />

            {/* ── Fallback ────────────────────────────────────── */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </ToastProvider>
      </AdminAuthProvider>
    </Router>
  );
}

export default App;
